// FPCaptainShop.js
// Franchise Player — the captain shop. Each entry buys one franchise player
// before the league's first kickoff. Prices are frozen when the league is
// created; injury status is live from the daily player sync. Purchases go
// through the fpBuyCaptain Cloud Function, which re-checks every rule.

import React, { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "./firebase";
import { FP_ACCENT, FP_ACCENT_DIM, POSITION_COLORS, formatDeadline, timeUntil, leagueStatus, entryLabel } from "./fp-theme";
import { PLAYER_POSITIONS, injuryInfo, formatMoney } from "./fp-config";

function InjuryTag({ injury }) {
  const info = injury ? injuryInfo(injury.designation, injury.returnDate) : null;
  if (!info) return null;
  return (
    <span
      title={`${injury.designation}${injury.note ? " · " + injury.note : ""}`}
      style={{
        fontSize: 10, fontWeight: 800, letterSpacing: 0.5, padding: "2px 6px", borderRadius: 4,
        background: info.short === "Q" ? "rgba(251,191,36,0.15)" : "rgba(229,57,53,0.15)",
        color: info.short === "Q" ? "#FBBF24" : "#F87171",
        border: `1px solid ${info.short === "Q" ? "rgba(251,191,36,0.4)" : "rgba(229,57,53,0.4)"}`,
      }}
    >
      {info.short}
    </span>
  );
}

function PosBadge({ pos }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4, minWidth: 26, textAlign: "center",
      color: POSITION_COLORS[pos] || "white", background: "rgba(255,255,255,0.06)",
      border: `1px solid ${(POSITION_COLORS[pos] || "#fff")}55`, display: "inline-block",
    }}>
      {pos}
    </span>
  );
}

// Inline "rename" for an entry card.
function RenameEntry({ entry, onDone }) {
  const [draft, setDraft] = useState(entry.name || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!draft.trim()) { setErr("Enter a name."); return; }
    setBusy(true);
    setErr("");
    try {
      await httpsCallable(getFunctions(), "fpRenameEntry")({ entryId: entry.id, name: draft });
      onDone();
    } catch (e) { setErr(e.message || "Rename failed."); }
    setBusy(false);
  };
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 6 }}>
      <div style={{ display: "flex", gap: 6 }}>
        <input className="input fp-input" value={draft} maxLength={30} autoFocus
          onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") onDone(); }}
          style={{ padding: "6px 10px", fontSize: 13 }} aria-label="Entry name" />
        <button onClick={save} disabled={busy} className="btn btn-fp btn-sm">{busy ? "..." : "Save"}</button>
        <button onClick={onDone} className="btn btn-ghost btn-sm">✕</button>
      </div>
      {err && <div style={{ fontSize: 12, color: "#F87171", marginTop: 4 }}>{err}</div>}
    </div>
  );
}

export default function FPCaptainShop({ league, myEntries, allEntries = [] }) {
  const [shop, setShop] = useState(null);
  const [pool, setPool] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(null); // entry id, or "new"
  const [search, setSearch] = useState("");
  const [pos, setPos] = useState("ALL");
  const [tierOnly, setTierOnly] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState(null);
  const [newName, setNewName] = useState("");     // confirmed name for the entry being created
  const [nameDraft, setNameDraft] = useState("");
  const [nameError, setNameError] = useState("");
  const [renaming, setRenaming] = useState(null); // entry id

  const shopOpen = leagueStatus(league) === "shop_open";
  const canAddEntry = myEntries.length < (league.maxEntries || 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [shopSnap, poolSnap] = await Promise.all([
          getDoc(doc(db, "fp_leagues", league.id, "shop", "prices")),
          getDoc(doc(db, "fp_playerPool", "current")),
        ]);
        if (cancelled) return;
        setShop(shopSnap.exists() ? shopSnap.data() : null);
        const byId = {};
        for (const p of (poolSnap.exists() && poolSnap.data().players) || []) byId[p.id] = p;
        setPool(byId);
      } catch (e) {
        console.error("Error loading shop:", e);
      }
      if (!cancelled) setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [league.id]);

  // Default selection: first entry, or "new" when there are none yet.
  useEffect(() => {
    if (selected && (selected === "new" || myEntries.some((e) => e.id === selected))) return;
    setSelected(myEntries[0]?.id || "new");
  }, [myEntries, selected]);

  const selectedEntry = myEntries.find((e) => e.id === selected) || null;

  const players = useMemo(() => {
    if (!shop) return [];
    const term = search.trim().toLowerCase();
    return (shop.list || [])
      .map((p) => ({ ...p, live: pool[p.id] || null }))
      .filter((p) => !(p.live?.injury && injuryInfo(p.live.injury.designation, p.live.injury.returnDate)?.longTerm))
      .filter((p) => !tierOnly || p.price >= league.captainFloor)
      .filter((p) => pos === "ALL" || p.pos === pos)
      .filter((p) => !term || p.name.toLowerCase().includes(term) || p.team.toLowerCase().includes(term));
  }, [shop, pool, search, pos, tierOnly, league.captainFloor]);

  const kickbackFor = (price) => Math.min(league.captainBudget - price, league.maxKickback);

  async function buy(player) {
    if (!shopOpen || busyId) return;
    setBusyId(player.id);
    setMessage(null);
    try {
      const res = await httpsCallable(getFunctions(), "fpBuyCaptain")({
        leagueId: league.id,
        playerId: player.id,
        entryId: selectedEntry ? selectedEntry.id : null,
        entryName: selectedEntry ? undefined : newName,
      });
      if (!selectedEntry) { setNewName(""); setNameDraft(""); }
      const kb = res.data.kickback;
      setSelected(res.data.entryId);
      setMessage({
        ok: true,
        text: `${player.name} is your franchise player${kb > 0 ? `, with +${formatMoney(kb)} added to your weekly budget` : ""}. You can change him until the shop closes.` +
          (res.data.lineupsReset ? " Your saved lineup was cleared, so set it again." : ""),
      });
    } catch (e) {
      // Name taken (someone grabbed it meanwhile): send them back to the name step.
      if (e.code === "functions/already-exists" || /already taken/.test(e.message || "")) {
        setNewName("");
        setNameError(e.message);
      } else {
        setMessage({ ok: false, text: e.message || "Purchase failed. Try again." });
      }
    }
    setBusyId(null);
  }

  function confirmName() {
    const name = nameDraft.replace(/\s+/g, " ").trim();
    if (!name) { setNameError("Enter a name for your entry."); return; }
    if (name.length > 30) { setNameError("Names can be up to 30 characters."); return; }
    if (allEntries.some((e) => (e.name || "").toLowerCase() === name.toLowerCase())) {
      setNameError(`"${name}" is already taken in this league. Pick another name.`);
      return;
    }
    setNameError("");
    setNewName(name);
  }

  const needsName = selected === "new" && !newName;

  if (!loaded) return <p style={{ color: "rgba(255,255,255,0.4)", textAlign: "center", padding: 32 }}>Loading the captain shop...</p>;
  if (!shop) return <p style={{ color: "rgba(255,255,255,0.5)", textAlign: "center", padding: 32 }}>This league doesn't have a captain shop.</p>;

  return (
    <div className="animate-fade-in">
      {/* Shop header */}
      <div className="card" style={{ padding: "16px 18px", marginBottom: 16, background: `linear-gradient(135deg, ${FP_ACCENT_DIM}, var(--navy-card))`, borderColor: "rgba(20,184,166,0.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, letterSpacing: 1.5, color: FP_ACCENT }}>CAPTAIN SHOP</div>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.65)", lineHeight: 1.5, maxWidth: 560 }}>
              Your budget is <b>{formatMoney(league.captainBudget)}</b>, enough for anyone. Every dollar you don't spend is added to your weekly
              budget all season, up to <b>{formatMoney(league.maxKickback)}</b>. Your franchise player scores <b>{league.captainMultiplier}x</b> and can't be replaced.
            </p>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: "rgba(255,255,255,0.4)" }}>{shopOpen ? "SHOP CLOSES" : "SHOP CLOSED"}</div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{formatDeadline(league.shopDeadline)}</div>
            {shopOpen && <div style={{ fontSize: 12, color: FP_ACCENT }}>in {timeUntil(league.shopDeadline)}</div>}
          </div>
        </div>
      </div>

      {/* My entries */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10, marginBottom: 16 }}>
        {myEntries.map((e) => {
          const active = selected === e.id;
          return (
            <div
              key={e.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelected(e.id)}
              onKeyDown={(ev) => { if (ev.target === ev.currentTarget && (ev.key === "Enter" || ev.key === " ")) setSelected(e.id); }}
              className="card"
              style={{
                textAlign: "left", cursor: "pointer", padding: "12px 14px",
                borderColor: active ? FP_ACCENT : "var(--border)",
                background: active ? FP_ACCENT_DIM : "var(--navy-card)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                <span style={{ fontWeight: 800, color: "white", fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entryLabel(e)}</span>
                {renaming !== e.id && (
                  <span role="button" tabIndex={0} title="Rename entry" aria-label={`Rename ${entryLabel(e)}`}
                    onClick={(ev) => { ev.stopPropagation(); setRenaming(e.id); }}
                    style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", cursor: "pointer", flexShrink: 0 }}>✏️ Rename</span>
                )}
              </div>
              {renaming === e.id && <RenameEntry entry={e} onDone={() => setRenaming(null)} />}
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                <PosBadge pos={e.captain.pos} />
                <span style={{ fontWeight: 600, color: "rgba(255,255,255,0.85)", fontSize: 13 }}>{e.captain.name}</span>
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 4 }}>
                {formatMoney(e.captain.price)} · weekly budget <span style={{ color: FP_ACCENT, fontWeight: 700 }}>{formatMoney(e.weeklyBudget)}</span>
              </div>
            </div>
          );
        })}
        {shopOpen && canAddEntry && (
          <button
            onClick={() => setSelected("new")}
            className="card"
            style={{
              cursor: "pointer", padding: "12px 14px", textAlign: "left",
              borderStyle: "dashed", borderColor: selected === "new" ? FP_ACCENT : "rgba(255,255,255,0.2)",
              background: selected === "new" ? FP_ACCENT_DIM : "transparent",
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "rgba(255,255,255,0.4)" }}>
              {myEntries.length === 0 ? "YOUR ENTRY" : `ENTRY ${myEntries.length + 1}`}
            </div>
            <div style={{ fontWeight: 700, color: FP_ACCENT, fontSize: 14, marginTop: 4 }}>
              {newName && selected === "new" ? newName : myEntries.length === 0 ? "+ Create your entry" : "+ Add an entry"}
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 4 }}>
              {myEntries.length} of {league.maxEntries} {league.maxEntries === 1 ? "entry" : "entries"} used
            </div>
          </button>
        )}
      </div>

      {!shopOpen && (
        <div className="card" style={{ padding: "14px 18px", marginBottom: 16, color: "rgba(255,255,255,0.6)", fontSize: 14 }}>
          The captain shop closed at the league's first kickoff. Franchise players are locked in for the season.
        </div>
      )}

      {message && (
        <div style={{
          fontSize: 13, padding: "10px 14px", borderRadius: 10, marginBottom: 12,
          color: message.ok ? FP_ACCENT : "#F87171",
          background: message.ok ? FP_ACCENT_DIM : "rgba(229,57,53,0.1)",
          border: `1px solid ${message.ok ? "rgba(20,184,166,0.35)" : "rgba(229,57,53,0.35)"}`,
        }}>
          {message.text}
        </div>
      )}

      {/* Step 1 for a new entry: name it */}
      {needsName && shopOpen && (
        <div className="card" style={{ padding: "20px 18px" }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: 1.5, marginBottom: 4 }}>
            {myEntries.length === 0 ? "NAME YOUR ENTRY" : `NAME ENTRY ${myEntries.length + 1}`}
          </div>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginBottom: 12, lineHeight: 1.5 }}>
            This is the name the league sees on standings all season. You can rename it any time.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input className="input fp-input" placeholder="Entry name" maxLength={30} value={nameDraft} autoFocus
              onChange={(e) => { setNameDraft(e.target.value); setNameError(""); }}
              onKeyDown={(e) => e.key === "Enter" && confirmName()}
              style={{ flex: "1 1 220px" }} aria-label="Entry name" />
            <button onClick={confirmName} className="btn btn-fp">Continue to the shop →</button>
          </div>
          {nameError && <p className="error-text" style={{ marginTop: 8 }}>{nameError}</p>}
        </div>
      )}

      {/* Filters */}
      {!(needsName && shopOpen) && (
      <div className="card" style={{ padding: 16 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          <input
            type="text"
            placeholder="Search players or teams..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input fp-input"
            style={{ flex: "1 1 220px" }}
          />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {["ALL", ...PLAYER_POSITIONS].map((p) => (
              <button key={p} onClick={() => setPos(p)} className={pos === p ? "btn btn-fp btn-sm" : "btn btn-ghost btn-sm"}>{p}</button>
            ))}
          </div>
        </div>
        {selected === "new" && newName && (
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.65)", marginBottom: 10 }}>
            Buying for <b style={{ color: "white" }}>{newName}</b>{" "}
            <span role="button" tabIndex={0} onClick={() => { setNameDraft(newName); setNewName(""); }}
              style={{ color: FP_ACCENT, cursor: "pointer", fontSize: 12 }}>change name</span>
          </div>
        )}
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "rgba(255,255,255,0.55)", marginBottom: 12, cursor: "pointer" }}>
          <input type="checkbox" checked={tierOnly} onChange={(e) => setTierOnly(e.target.checked)} />
          Franchise tier only ({formatMoney(league.captainFloor)} and up, where every dollar saved comes back)
        </label>

        {/* Player list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 560, overflowY: "auto" }}>
          {players.length === 0 && (
            <p style={{ color: "rgba(255,255,255,0.4)", padding: 16 }}>No players match your filters.</p>
          )}
          {players.map((p) => {
            const kb = kickbackFor(p.price);
            const isCurrent = selectedEntry?.captain?.playerId === p.id;
            const bye = p.live?.bye;
            return (
              <div key={p.id} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                background: isCurrent ? FP_ACCENT_DIM : "rgba(255,255,255,0.03)",
                border: `1px solid ${isCurrent ? "rgba(20,184,166,0.4)" : "transparent"}`,
                borderRadius: 8, padding: "10px 12px",
              }}>
                <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 10 }}>
                  <PosBadge pos={p.pos} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      {p.name} <InjuryTag injury={p.live?.injury} />
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>
                      {p.team}
                      {p.posAdp ? ` · ${p.posAdp} by ADP` : ""}
                      {bye ? ` · bye wk ${bye}` : ""}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{formatMoney(p.price)}</div>
                    <div style={{ fontSize: 11, color: kb > 0 ? FP_ACCENT : "rgba(255,255,255,0.3)" }}>
                      {kb > 0 ? `+${formatMoney(kb)}/wk` : "no kickback"}
                    </div>
                  </div>
                  {shopOpen && (
                    <button
                      onClick={() => buy(p)}
                      disabled={!!busyId || isCurrent || (selected === "new" && !canAddEntry)}
                      className={isCurrent ? "btn btn-ghost btn-sm" : "btn btn-fp btn-sm"}
                      style={{ minWidth: 72, justifyContent: "center" }}
                    >
                      {busyId === p.id ? "..." : isCurrent ? "Yours" : selectedEntry ? "Switch" : "Buy"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}
